// SATpracticeTest.js
// SAT Practice Test Component - Provides real SAT practice questions from OpenSAT database
// Features: Multiple domains (Math, Reading, Writing), difficulty levels, progress tracking

import React, { useState, useEffect, useRef } from 'react';
import './SATpracticeTest.css';
import 'katex/dist/katex.min.css';
import katex from 'katex';

const SATPracticeTest = ({ onComplete, onClose, savedProgress = null }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showExplanation, setShowExplanation] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [questionsPerTest, setQuestionsPerTest] = useState(10);
  const [testStarted, setTestStarted] = useState(false);
  const [score, setScore] = useState(null);
  const [reviewMode, setReviewMode] = useState(false);

  // Timer states
  const [testMode, setTestMode] = useState('focused'); // 'focused' or 'full-length'
  const [timedMode, setTimedMode] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [timeExpired, setTimeExpired] = useState(false);
  const timerIntervalRef = useRef(null);

  // SAT Question Bank API and Local Questions
  const SAT_API_URL = 'https://api.jsonsilo.com/public/942c3c3b-3a0c-4be3-81c2-12029def19f5';

  // Import local Reading/Writing questions
  const satReadingWritingQuestions = require('./data/sat-reading-writing.json');

  // Fetch questions from OpenSAT database and merge with local questions
  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch Math questions from API
      const response = await fetch(SAT_API_URL);
      if (!response.ok) {
        throw new Error('Failed to fetch SAT Math questions');
      }

      const data = await response.json();
      console.log('Raw API response:', data);
      console.log('Response type:', typeof data);

      // Handle different response formats for Math questions
      let mathQuestions = [];
      if (Array.isArray(data)) {
        mathQuestions = data;
      } else if (data && typeof data === 'object') {
        // Check if data has a questions property
        if (Array.isArray(data.questions)) {
          mathQuestions = data.questions;
        } else if (Array.isArray(data.data)) {
          mathQuestions = data.data;
        } else {
          // If it's an object with values, try to extract them
          const values = Object.values(data);
          if (values.length > 0 && Array.isArray(values[0])) {
            mathQuestions = values[0];
          } else {
            throw new Error('Invalid data format received from API');
          }
        }
      } else {
        throw new Error('Invalid data format received from API');
      }

      console.log('Math questions from API:', mathQuestions.length);
      console.log('Reading/Writing questions from local:', satReadingWritingQuestions.length);

      // Combine Math questions from API with local Reading/Writing questions
      const allQuestions = [...mathQuestions, ...satReadingWritingQuestions];

      console.log('Total SAT questions:', allQuestions.length);

      if (allQuestions.length === 0) {
        throw new Error('No questions available');
      }

      setQuestions(allQuestions);
      setLoading(false);
    } catch (err) {
      console.error('Error loading SAT questions:', err);
      setError(`Failed to load SAT practice questions: ${err.message}`);
      setLoading(false);
    }
  };

  // Get available domains from questions
  const getAvailableDomains = () => {
    if (!Array.isArray(questions) || questions.length === 0) {
      return [];
    }
    const domains = [...new Set(questions.map(q => q.domain))];
    return domains.sort();
  };

  // Filter questions based on selected criteria
  const getFilteredQuestions = () => {
    if (!Array.isArray(questions) || questions.length === 0) {
      return [];
    }
    let filtered = [...questions];

    if (selectedDomain !== 'all') {
      filtered = filtered.filter(q => q.domain === selectedDomain);
    }

    if (selectedDifficulty !== 'all') {
      filtered = filtered.filter(q => q.difficulty === selectedDifficulty);
    }

    // Shuffle and limit to requested number
    filtered = filtered.sort(() => Math.random() - 0.5).slice(0, questionsPerTest);

    return filtered;
  };

  // Render LaTeX math in text
  const renderMath = (text) => {
    if (!text) return '';

    try {
      // Replace LaTeX math delimiters with rendered HTML
      let rendered = text;

      // Handle display math $$...$$
      rendered = rendered.replace(/\$\$([\s\S]+?)\$\$/g, (match, math) => {
        try {
          return katex.renderToString(math, { displayMode: true, throwOnError: false });
        } catch (e) {
          return match;
        }
      });

      // Handle inline math $...$
      rendered = rendered.replace(/\$([^$]+?)\$/g, (match, math) => {
        try {
          return katex.renderToString(math, { displayMode: false, throwOnError: false });
        } catch (e) {
          return match;
        }
      });

      return rendered;
    } catch (e) {
      console.error('Math rendering error:', e);
      return text;
    }
  };

  // Calculate time limit based on test mode
  const calculateTimeLimit = (numQuestions, mode) => {
    if (mode === 'full-length') {
      // SAT Math section: ~70 minutes for 44 questions (roughly 95 seconds per question)
      return numQuestions * 95;
    } else {
      // Focused practice: 90 seconds per question
      return numQuestions * 90;
    }
  };

  // Format time for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Timer effect
  useEffect(() => {
    if (testStarted && timedMode && timeRemaining > 0 && !testCompleted && !reviewMode) {
      timerIntervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setTimeExpired(true);
            clearInterval(timerIntervalRef.current);
            // Show notification
            alert('⏰ Time expired! You can still continue and complete the test.');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      };
    }
  }, [testStarted, timedMode, timeRemaining, testCompleted, reviewMode]);

  // Start the practice test
  const startTest = () => {
    const filtered = getFilteredQuestions();
    if (filtered.length === 0) {
      alert('No questions available for the selected criteria. Please adjust your filters.');
      return;
    }

    setQuestions(filtered);
    setTestStarted(true);
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
    setShowExplanation(false);
    setTestCompleted(false);
    setScore(null);
    setTimeExpired(false);

    // Set up timer if timed mode is enabled
    if (timedMode) {
      const limit = calculateTimeLimit(filtered.length, testMode);
      setTimeRemaining(limit);
    }
  };

  // Handle answer selection
  const selectAnswer = (choice) => {
    if (showExplanation) return; // Don't allow changing answer after viewing explanation

    setSelectedAnswers({
      ...selectedAnswers,
      [currentQuestionIndex]: choice
    });
  };

  // Check answer and show explanation
  const checkAnswer = () => {
    if (!selectedAnswers[currentQuestionIndex]) {
      alert('Please select an answer first.');
      return;
    }
    setShowExplanation(true);
  };

  // Move to next question
  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setShowExplanation(false);
    } else {
      finishTest();
    }
  };

  // Move to previous question
  const previousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setShowExplanation(false);
    }
  };

  // Finish the test and calculate score
  const finishTest = () => {
    const totalQuestions = questions.length;
    const answeredQuestions = Object.keys(selectedAnswers).length;

    if (answeredQuestions < totalQuestions) {
      const confirmFinish = window.confirm(`You have answered ${answeredQuestions} out of ${totalQuestions} questions. Are you sure you want to finish?`);
      if (!confirmFinish) return;
    }

    let correctCount = 0;
    questions.forEach((q, index) => {
      if (selectedAnswers[index] === q.question.correct_answer) {
        correctCount++;
      }
    });

    const percentage = Math.round((correctCount / totalQuestions) * 100);
    setScore({ correct: correctCount, total: totalQuestions, percentage });
    setTestCompleted(true);
    setReviewMode(false);

    // Notify parent component
    if (onComplete) {
      onComplete({
        score: percentage,
        correct: correctCount,
        total: totalQuestions,
        domain: selectedDomain,
        difficulty: selectedDifficulty
      });
    }
  };

  // Review answers
  const startReview = () => {
    setReviewMode(true);
    setCurrentQuestionIndex(0);
    setShowExplanation(true);
  };

  // Restart test
  const restartTest = () => {
    setTestStarted(false);
    setTestCompleted(false);
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
    setShowExplanation(false);
    setScore(null);
    setReviewMode(false);
  };

  if (loading) {
    return (
      <div className="sat-practice-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading SAT Practice Questions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sat-practice-container">
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={fetchQuestions} className="retry-button">Retry</button>
          {onClose && <button onClick={onClose} className="close-button">Close</button>}
        </div>
      </div>
    );
  }

  // Test Setup Screen
  if (!testStarted) {
    const availableDomains = getAvailableDomains();

    return (
      <div className="sat-practice-container">
        <div className="sat-setup-screen">
          <div className="sat-header">
            <h2>🎓 SAT Practice Test</h2>
            <p>Real SAT questions from the OpenSAT database</p>
          </div>

          <div className="sat-setup-options">
            <div className="setup-section">
              <label>Test Mode:</label>
              <select
                value={testMode}
                onChange={(e) => {
                  const mode = e.target.value;
                  setTestMode(mode);
                  // Auto-adjust question count for full-length
                  if (mode === 'full-length') {
                    setQuestionsPerTest(44); // SAT Math section has 44 questions
                  } else {
                    setQuestionsPerTest(10);
                  }
                }}
                className="sat-select"
              >
                <option value="focused">Focused Practice</option>
                <option value="full-length">Full-Length Test</option>
              </select>
              <p className="setup-hint">
                {testMode === 'focused'
                  ? 'Practice specific skills with customizable question count'
                  : 'Simulate a real SAT Math section (44 questions, ~70 minutes)'}
              </p>
            </div>

            <div className="setup-section">
              <label>Select Domain:</label>
              <select
                value={selectedDomain}
                onChange={(e) => setSelectedDomain(e.target.value)}
                className="sat-select"
                disabled={testMode === 'full-length'}
              >
                <option value="all">All Math Domains (Mixed)</option>
                {availableDomains.map(domain => (
                  <option key={domain} value={domain}>{domain}</option>
                ))}
              </select>
              {availableDomains.length === 0 && (
                <p className="setup-hint info">✅ Available: Math questions (from OpenSAT API) and Reading/Writing questions (20 curated questions)</p>
              )}
            </div>

            <div className="setup-section">
              <label>Select Difficulty:</label>
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                className="sat-select"
                disabled={testMode === 'full-length'}
              >
                <option value="all">All Difficulties</option>
                <option value="Easy">Easy</option>
                <option value="Medium">Medium</option>
                <option value="Hard">Hard</option>
              </select>
            </div>

            {testMode === 'focused' && (
              <div className="setup-section">
                <label>Number of Questions:</label>
                <select
                  value={questionsPerTest}
                  onChange={(e) => setQuestionsPerTest(Number(e.target.value))}
                  className="sat-select"
                >
                  <option value="5">5 Questions (Quick Practice)</option>
                  <option value="10">10 Questions (Short Test)</option>
                  <option value="20">20 Questions (Medium Test)</option>
                  <option value="30">30 Questions (Long Test)</option>
                  <option value="44">44 Questions (Full Section)</option>
                </select>
              </div>
            )}

            <div className="setup-section">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={timedMode}
                  onChange={(e) => setTimedMode(e.target.checked)}
                  className="sat-checkbox"
                />
                <span>Timed Mode</span>
              </label>
              <p className="setup-hint">
                {timedMode
                  ? `Time limit: ${formatTime(calculateTimeLimit(questionsPerTest, testMode))} (${testMode === 'full-length' ? '~95' : '90'} seconds per question)`
                  : 'Practice at your own pace without time pressure'}
              </p>
            </div>

            <div className="setup-info">
              <p><strong>Questions Selected:</strong> {questionsPerTest}</p>
              <p><strong>Total Available:</strong> {questions.length}</p>
              <p><strong>Domains:</strong> {availableDomains.length} Math categories</p>
              {timedMode && (
                <p><strong>Time Limit:</strong> {formatTime(calculateTimeLimit(questionsPerTest, testMode))}</p>
              )}
            </div>

            <div className="setup-actions">
              <button onClick={startTest} className="start-test-button">
                Start Practice Test
              </button>
              {onClose && (
                <button onClick={onClose} className="cancel-button">
                  Cancel
                </button>
              )}
            </div>
          </div>

          <div className="sat-info-footer">
            <p><em>Questions powered by OpenSAT - A free, open-source SAT question database</em></p>
          </div>
        </div>
      </div>
    );
  }

  // Test Completed Screen
  if (testCompleted && !reviewMode) {
    return (
      <div className="sat-practice-container">
        <div className="sat-results-screen">
          <div className="results-header">
            <h2>🎉 Test Completed!</h2>
          </div>

          <div className="results-score">
            <div className="score-circle">
              <div className="score-percentage">{score.percentage}%</div>
              <div className="score-detail">{score.correct} / {score.total}</div>
            </div>
          </div>

          <div className="results-breakdown">
            <h3>Performance Summary</h3>
            <div className="breakdown-item">
              <span>Correct Answers:</span>
              <strong>{score.correct}</strong>
            </div>
            <div className="breakdown-item">
              <span>Incorrect Answers:</span>
              <strong>{score.total - score.correct}</strong>
            </div>
            <div className="breakdown-item">
              <span>Domain:</span>
              <strong>{selectedDomain === 'all' ? 'Mixed' : selectedDomain}</strong>
            </div>
            <div className="breakdown-item">
              <span>Difficulty:</span>
              <strong>{selectedDifficulty === 'all' ? 'Mixed' : selectedDifficulty}</strong>
            </div>
          </div>

          <div className="results-actions">
            <button onClick={startReview} className="review-button">
              Review Answers
            </button>
            <button onClick={restartTest} className="restart-button">
              Take Another Test
            </button>
            {onClose && (
              <button onClick={onClose} className="done-button">
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Question Screen
  const currentQuestion = questions[currentQuestionIndex];

  // Safety check - if no valid question, show error
  if (!currentQuestion || !currentQuestion.question) {
    return (
      <div className="sat-practice-container">
        <div className="error-message">
          <h3>Error</h3>
          <p>Unable to load question. Please try restarting the test.</p>
          <button onClick={restartTest} className="retry-button">Restart Test</button>
          {onClose && <button onClick={onClose} className="close-button">Close</button>}
        </div>
      </div>
    );
  }

  const userAnswer = selectedAnswers[currentQuestionIndex];
  const isCorrect = userAnswer === currentQuestion.question.correct_answer;

  return (
    <div className="sat-practice-container">
      <div className="sat-test-screen">
        {/* Progress Bar */}
        <div className="sat-progress">
          <div className="progress-info">
            <span>Question {currentQuestionIndex + 1} of {questions.length}</span>
            <span className="domain-badge">{currentQuestion.domain}</span>
            <span className="difficulty-badge">{currentQuestion.difficulty}</span>
            {timedMode && (
              <span className={`timer-badge ${timeRemaining < 60 ? 'timer-warning' : timeExpired ? 'timer-expired' : ''}`}>
                ⏱️ {formatTime(timeRemaining)}
              </span>
            )}
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Question Content */}
        <div className="sat-question-content">
          {currentQuestion.question.paragraph && currentQuestion.question.paragraph !== 'null' && (
            <div className="question-paragraph">
              <p dangerouslySetInnerHTML={{ __html: renderMath(currentQuestion.question.paragraph) }}></p>
            </div>
          )}

          <div className="question-text">
            <p dangerouslySetInnerHTML={{ __html: renderMath(currentQuestion.question.question) }}></p>
          </div>

          {/* Answer Choices */}
          <div className="answer-choices">
            {Object.entries(currentQuestion.question.choices).map(([choice, text]) => {
              const isSelected = userAnswer === choice;
              const isCorrectChoice = choice === currentQuestion.question.correct_answer;
              const showCorrectness = showExplanation || reviewMode;

              let choiceClass = 'answer-choice';
              if (isSelected) choiceClass += ' selected';
              if (showCorrectness) {
                if (isCorrectChoice) choiceClass += ' correct';
                if (isSelected && !isCorrect) choiceClass += ' incorrect';
              }

              return (
                <div
                  key={choice}
                  className={choiceClass}
                  onClick={() => !reviewMode && selectAnswer(choice)}
                >
                  <span className="choice-letter">{choice}</span>
                  <span className="choice-text" dangerouslySetInnerHTML={{ __html: renderMath(text) }}></span>
                  {showCorrectness && isCorrectChoice && <span className="check-icon">✓</span>}
                  {showCorrectness && isSelected && !isCorrect && <span className="x-icon">✗</span>}
                </div>
              );
            })}
          </div>

          {/* Explanation */}
          {(showExplanation || reviewMode) && (
            <div className={`explanation-box ${isCorrect ? 'correct-explanation' : 'incorrect-explanation'}`}>
              <h4>{isCorrect ? '✓ Correct!' : '✗ Incorrect'}</h4>
              <p><strong>Correct Answer:</strong> {currentQuestion.question.correct_answer}</p>
              {currentQuestion.question.explanation && currentQuestion.question.explanation !== 'null' && (
                <div className="explanation-text">
                  <p><strong>Explanation:</strong></p>
                  <p dangerouslySetInnerHTML={{ __html: renderMath(currentQuestion.question.explanation) }}></p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="sat-navigation">
          <button
            onClick={previousQuestion}
            disabled={currentQuestionIndex === 0}
            className="nav-button"
          >
            ← Previous
          </button>

          {!showExplanation && !reviewMode && (
            <button onClick={checkAnswer} className="check-button">
              Check Answer
            </button>
          )}

          {(showExplanation || reviewMode) && currentQuestionIndex < questions.length - 1 && (
            <button onClick={nextQuestion} className="nav-button">
              Next →
            </button>
          )}

          {(showExplanation || reviewMode) && currentQuestionIndex === questions.length - 1 && !reviewMode && (
            <button onClick={finishTest} className="finish-button">
              Finish Test
            </button>
          )}

          {reviewMode && currentQuestionIndex === questions.length - 1 && (
            <button onClick={() => setTestCompleted(true)} className="nav-button">
              Back to Results
            </button>
          )}
        </div>

        {/* Question Navigator */}
        <div className="question-navigator">
          <p>Jump to Question:</p>
          <div className="question-dots">
            {questions.map((_, index) => {
              const answered = selectedAnswers.hasOwnProperty(index);
              const isCurrent = index === currentQuestionIndex;
              let dotClass = 'question-dot';
              if (isCurrent) dotClass += ' current';
              if (answered) dotClass += ' answered';
              if (reviewMode) {
                const isAnswerCorrect = selectedAnswers[index] === questions[index].question.correct_answer;
                dotClass += isAnswerCorrect ? ' dot-correct' : ' dot-incorrect';
              }

              return (
                <button
                  key={index}
                  className={dotClass}
                  onClick={() => {
                    setCurrentQuestionIndex(index);
                    setShowExplanation(reviewMode);
                  }}
                  title={`Question ${index + 1}`}
                >
                  {index + 1}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SATPracticeTest;
